import { Box, Container, Typography } from "@mui/material";
import referralprogram from "../../../assets/images/articlesimg/ReferralProgramArticle.jpg";
import theme from "../../../theme/Theme";

const ReferralProgramArticle = () => {
  return (
    <Box sx={{ mb: 4 }}>
      <Container sx={styles.container}>
        <Typography variant="h1" sx={styles.title}>
          Earn Even If You Don't Win: Unlocking Earnings Through SWSS Draw's
          Referral Program
        </Typography>

        <Box
          component="img"
          src={referralprogram}
          alt="Blockchain Lottery"
          sx={styles.heroImage}
        />

        <Box sx={styles.contentBox}>
          <Typography sx={styles.sectionText}>
            Participating in lotteries can be thrilling, but traditionally,
            there's a clear downside: most participants don't win. SWSS Draw
            revolutionizes this dynamic by introducing a powerful referral
            program, enabling every participant to earn rewards—even without
            hitting the jackpot.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Limitations of Traditional Lottery Participation
          </Typography>
          <Typography sx={styles.sectionText}>
            Conventional lottery systems offer binary outcomes: participants
            either win or lose. This rigid structure leaves most players
            empty-handed, potentially discouraging ongoing participation.
            Without alternative incentives or rewards, traditional lotteries can
            quickly lose their appeal, despite the initial excitement.
          </Typography>

          <Typography sx={styles.sectionText}>
            Recognizing this gap, SWSS Draw reshapes the lottery experience by
            introducing a multi-level referral program. Participants can now
            earn ongoing income simply by referring friends, family, and
            acquaintances to participate in the lottery.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            The Power of Referral Programs
          </Typography>
          <Typography sx={styles.sectionText}>
            Referral programs are increasingly common in today's digital age due
            to their mutual benefits for users and platforms alike. SWSS Draw
            amplifies these advantages, creating additional opportunities for
            community engagement and earning potential.
          </Typography>

          <Typography sx={styles.sectionText}>
            Here's how the SWSS Draw referral program works:
          </Typography>
          <Box component="ul" sx={styles.orderedList}>
            <li>
              <strong>Share Your Link:</strong> Each participant receives a
              unique referral link, easily shareable via social media, messaging
              apps, or email.
            </li>
            <li>
              <strong>Earn Rewards Automatically:</strong> Every time someone
              uses your referral link to purchase a lottery ticket, you
              automatically earn a percentage of the ticket's purchase price.
            </li>
            <li>
              <strong>Multi-Level Earnings:</strong> Unlike traditional
              single-level referral systems, SWSS Draw offers multi-level
              referrals, meaning you earn not just from your direct referrals,
              but also from referrals made by people you've invited.
            </li>
          </Box>
          <Typography sx={styles.sectionText}>
            This multi-tiered system exponentially increases your earning
            potential, creating significant passive income streams.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            How SWSS Draw's Referral System Enhances Earnings
          </Typography>
          <Typography sx={styles.sectionText}>
            The structure of SWSS Draw's referral program is designed to
            maximize earnings opportunities:
          </Typography>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            Immediate and Transparent Rewards
          </Typography>
          <Typography sx={styles.sectionText}>
            All referral rewards are instantly recorded on the blockchain,
            providing immediate verification and transparency. Participants can
            confidently track their earnings in real-time.
          </Typography>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            Recurring Earnings
          </Typography>
          <Typography sx={styles.sectionText}>
            The referral program isn't a one-time deal; you earn rewards
            continually, as long as your referrals keep participating.
          </Typography>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            No Cap on Earnings
          </Typography>
          <Typography sx={styles.sectionText}>
            There are no limits on how many people you can refer, allowing for
            endless earning potential based purely on your network and
            engagement.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Ensuring Transparency Through Blockchain
          </Typography>
          <Typography sx={styles.sectionText}>
            One distinguishing feature of SWSS Draw's referral system is its
            reliance on blockchain technology. Every transaction, including
            ticket purchases and referral rewards, is securely and transparently
            recorded on the blockchain ledger. This ensures that all
            participants can independently verify their referral earnings,
            eliminating any doubts about fairness or transparency.
          </Typography>

          <Typography sx={styles.sectionText}>
            Participants can easily view their accumulated earnings and verify
            transactions on widely trusted platforms like Binance Smart Chain's
            explorer.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Boosting Community Participation
          </Typography>
          <Typography sx={styles.sectionText}>
            The SWSS Draw referral program significantly encourages community
            growth and participation. It not only incentivizes participants to
            share the lottery platform but also fosters stronger community ties
            and engagement, creating a vibrant, interconnected user base.
          </Typography>

          <Typography sx={styles.sectionText}>
            Referral programs inherently amplify user engagement and platform
            growth. According to a Nielsen study, 92% of consumers trust
            recommendations from friends and family over all other forms of
            advertising . SWSS Draw leverages this powerful insight, enabling
            users to directly benefit from their social connections.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            How to Get Started with the Referral Program
          </Typography>
          <Typography sx={styles.sectionText}>
            Getting started with SWSS Draw's referral program is straightforward
            and user-friendly:
          </Typography>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            1. Create an Account
          </Typography>
          <Box component="ul" sx={styles.unorderedList}>
            <li>
              Simply connect your crypto wallet to the SWSS Draw platform.
            </li>
          </Box>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            2. Access Your Unique Link
          </Typography>
          <Box component="ul" sx={styles.unorderedList}>
            <li>
              Your personalized referral link is readily available within your
              user dashboard.
            </li>
          </Box>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            3. Share Widely
          </Typography>
          <Box component="ul" sx={styles.unorderedList}>
            <li>
              Spread your referral link across social media platforms, messaging
              apps, or personal networks.
            </li>
          </Box>

          <Typography variant="h3" sx={styles.subsectionTitle}>
            4. Track Your Earnings
          </Typography>
          <Box component="ul" sx={styles.unorderedList}>
            <li>
              Watch your earnings accumulate as your network expands and
              participates.
            </li>
          </Box>
          <Typography sx={styles.sectionText}>
            The ease of participation, coupled with the attractive earning
            potential, makes the referral program a powerful tool for anyone
            looking to generate additional income.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            The Dual Advantage: Play and Earn
          </Typography>
          <Typography sx={styles.sectionText}>
            The beauty of the SWSS Draw referral program lies in its dual
            advantage. Participants can enjoy the excitement of the lottery
            while simultaneously benefiting financially from referrals, even if
            they do not directly win any draws themselves.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Real-World Impact of Referral Earnings
          </Typography>
          <Typography sx={styles.sectionText}>
            The multi-level referral system significantly impacts participants'
            real-world earnings potential. Many participants have successfully
            leveraged their networks to generate steady, substantial earnings,
            highlighting the practical benefits of engagement beyond lottery
            wins.
          </Typography>

          <Typography sx={styles.sectionText}>
            SWSS Draw's commitment to transparency ensures that referral
            earnings are always accurately and clearly reported, further
            increasing participant trust and satisfaction.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Explore Further
          </Typography>
          <Typography sx={styles.sectionText}>
            For participants eager to delve deeper into how referral earnings
            can enhance their lottery experience, SWSS Draw provides detailed
            information on the Referral Earnings page. This resource offers
            comprehensive insights into maximizing referral earnings and clearly
            outlines the multi-level referral structure.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Embrace the Future of Lottery Participation
          </Typography>
          <Typography sx={styles.sectionText}>
            SWSS Draw's innovative approach to lotteries goes beyond traditional
            prize-winning models, creating a holistic experience that rewards
            community engagement and networking. By introducing this
            referral-based earning system, SWSS Draw ensures that participation
            remains rewarding and exciting for everyone involved.
          </Typography>

          <Typography sx={styles.sectionText}>
            Participants no longer need to rely solely on luck to benefit from
            lottery participation. Instead, SWSS Draw offers consistent and
            ongoing earnings potential through its referral program,
            significantly enhancing the overall lottery experience.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Conclusion
          </Typography>
          <Typography sx={styles.sectionText}>
            SWSS Draw's multi-level referral program represents a significant
            evolution in lottery participation, ensuring every participant has
            the chance to earn, irrespective of traditional lottery outcomes.
            Blockchain transparency, coupled with the power of social
            connections, ensures participants enjoy a genuinely rewarding
            experience.
          </Typography>
          <Typography sx={styles.sectionText}>
            Join the thriving SWSS Draw community today by sharing your referral
            link, expanding your network, and unlocking continuous earning
            opportunities, even if luck isn't on your side during a particular
            draw. Discover firsthand the power of community-driven earnings with
            the revolutionary SWSS Draw lottery platform.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const styles = {
  container: {
    py: 4,
    maxWidth: "1200px",
  },
  title: {
    fontSize: { xs: "1.8rem", md: "2.5rem" },
    fontWeight: 700,
    color: theme.palette.text.primary,
    mb: 3,
    textAlign: "center",
    fontFamily: '"Proxima Nova", sans-serif',
  },
  heroImage: {
    width: "100%",
    position: "relative",
    aspectRatio: { xs: "16 / 9", md: "17 / 9" }, // or whatever ratio fits best
    mb: 4,
    borderRadius: 2,
    overflow: "hidden",
  },
  contentBox: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    boxShadow: 1,
    p: { xs: 2, md: 4 },
  },
  sectionTitle: {
    fontSize: { xs: "1.5rem", md: "1.8rem" },
    fontWeight: 600,
    color: "black",
    fontFamily: '"Proxima Nova", sans-serif',
    mt: 3,
    mb: 3,
  },
  subsectionTitle: {
    fontSize: { xs: "1.2rem", md: "1.4rem" },
    fontWeight: 600,
    fontFamily: '"Proxima Nova", sans-serif',
    color: "black", // theme.palette.text.primary,
    mt: 3,
    mb: 1,
  },
  sectionText: {
    fontSize: { xs: "0.9rem", md: "1.2rem" },
    fontFamily: '"Proxima Nova", sans-serif',
    fontWeight: 400,
    lineHeight: 1.6,
    mb: 2,
    colour: "black",
  },
  orderedList: {
    pl: 3,
    mb: 2,

    "& li": {
      mb: 1,
      fontSize: { xs: "0.9rem", md: "1.1rem" },
      fontFamily: '"Proxima Nova", sans-serif',
    },
    listStyleType: "decimal !important",
  },
  unorderedList: {
    pl: 6,
    mb: 2,
    "& li": {
      mb: 1,
      fontSize: { xs: "0.9rem", md: "1.1rem" },
      fontFamily: '"Proxima Nova", sans-serif',
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    my: 3,
    overflowX: "auto",
    display: "block",
    fontFamily: '"Proxima Nova", sans-serif',
    "& th, & td": {
      border: `1px solid ${theme.palette.divider}`,
      p: { xs: 1, md: 1.1 },
      fontSize: { xs: "0.8rem", md: "1.1rem" },
      textAlign: "left",
      fontFamily: '"Proxima Nova", sans-serif',
    },
    "& th": {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
      fontFamily: '"Proxima Nova", sans-serif',
    },
    [theme.breakpoints.up("md")]: {
      display: "table",
    },
  },
  stepImage: {
    width: { xs: "100%", md: "70%" },
    mx: "auto",
    mt: 2,
    mb: 4,
    aspectRatio: { xs: "16 / 9", md: "12 / 7" },
    borderRadius: 3,
    overflow: "hidden",

    boxShadow: "0 6px 18px rgba(0, 188, 212, 0.3)",
    objectFit: "cover",
    display: "block",
  },
};

export default ReferralProgramArticle;
